import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  Plus, Loader2, Trash2, Edit3, Download, Send, X, Upload, PenTool,
  ClipboardList, Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(Number(n || 0));

const STATUS_LABELS = {
  received:    { label: "Ontvangen",    cls: "bg-blue-100 text-blue-800" },
  in_progress: { label: "In reparatie", cls: "bg-amber-100 text-amber-900" },
  ready:       { label: "Gereed",       cls: "bg-emerald-100 text-emerald-800" },
  picked_up:   { label: "Opgehaald",    cls: "bg-neutral-200 text-neutral-700" },
  canceled:    { label: "Geannuleerd",  cls: "bg-rose-100 text-rose-800" },
};

const StatusPill = ({ s }) => {
  const m = STATUS_LABELS[s] || STATUS_LABELS.received;
  return <span className={`px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${m.cls}`}>{m.label}</span>;
};

// ===== tab =====
const RepairsTab = ({ secret }) => {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // new or repair id
  const [viewingId, setViewingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/admin/repairs`, { params: { secret } }),
        axios.get(`${API}/admin/customers`, { params: { secret } }),
      ]);
      setList(r.data); setCustomers(c.data);
    } finally { setLoading(false); }
  }, [secret]);

  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    try {
      const r = await axios.post(`${API}/admin/repairs`, payload, { params: { secret } });
      setEditing(null);
      await load();
      setViewingId(r.data.id); // open detail so they can add photos + signature
    } catch (e) {
      alert("Aanmaken mislukt: " + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Reparatieticket verwijderen?")) return;
    await axios.delete(`${API}/admin/repairs/${id}`, { params: { secret } });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-neutral-500">{list.length} reparatieticket{list.length === 1 ? "" : "s"}</p>
        <button
          onClick={() => setEditing("new")}
          className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-black"
          data-testid="repair-new-btn"
        ><Plus size={14}/>Nieuwe inname</button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-neutral-500 text-sm py-10 justify-center">
          <Loader2 size={14} className="animate-spin"/> Laden...
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-neutral-200">
          <ClipboardList size={24} className="mx-auto text-neutral-300 mb-2"/>
          <p className="text-sm text-neutral-500">Nog geen reparatietickets.</p>
        </div>
      ) : (
        <div className="border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Bon-nr.</th>
                <th className="px-4 py-3">Klant</th>
                <th className="px-4 py-3">Apparaat</th>
                <th className="px-4 py-3">Inname</th>
                <th className="px-4 py-3">Foto's</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {list.map((r) => (
                <tr key={r.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-mono text-xs">{r.ticket_number}</td>
                  <td className="px-4 py-3">{r.customer_name}</td>
                  <td className="px-4 py-3 text-neutral-700">{r.device_make} {r.device_model}</td>
                  <td className="px-4 py-3 text-neutral-600 text-xs">{(r.intake_date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.photo_count || 0}</td>
                  <td className="px-4 py-3"><StatusPill s={r.status}/></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setViewingId(r.id)} className="text-neutral-600 hover:text-neutral-900 mr-3" title="Bekijken / bewerken" data-testid={`repair-view-${r.id}`}><Eye size={14}/></button>
                    <button onClick={() => remove(r.id)} className="text-neutral-600 hover:text-rose-700" title="Verwijderen" data-testid={`repair-delete-${r.id}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing === "new" && (
        <NewRepairDialog customers={customers} onClose={() => setEditing(null)} onCreate={create}/>
      )}

      {viewingId && (
        <RepairDetailDialog
          repairId={viewingId}
          secret={secret}
          onClose={() => { setViewingId(null); load(); }}
        />
      )}
    </div>
  );
};

// ===== new repair dialog =====
const ACCESSORY_OPTIONS = ["Oplader", "Kabel", "Tas", "Hoes", "Muis", "Toetsenbord", "Dock", "USB-stick"];

const NewRepairDialog = ({ customers, onClose, onCreate }) => {
  const [f, setF] = useState({
    customer_id: "", device_make: "", device_model: "", device_serial: "",
    device_password: "", accessories: [], complaint: "", estimate: 0, notes: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleAcc = (a) => setF((p) => ({
    ...p,
    accessories: p.accessories.includes(a) ? p.accessories.filter((x) => x !== a) : [...p.accessories, a],
  }));

  const submit = (e) => {
    e.preventDefault();
    if (!f.customer_id) { alert("Kies een klant"); return; }
    if (!f.complaint.trim()) { alert("Klacht / omschrijving is verplicht"); return; }
    onCreate({ ...f, estimate: parseFloat(f.estimate) || 0 });
  };

  return (
    <Modal title="Nieuwe reparatie-inname" onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs text-neutral-600 uppercase tracking-wider">Klant *</label>
          <select
            value={f.customer_id} onChange={(e) => set("customer_id", e.target.value)} required
            className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
            data-testid="repair-customer-select"
          >
            <option value="">— Kies klant —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name ? `${c.company_name} — ${c.name}` : c.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Merk" value={f.device_make} onChange={(v) => set("device_make", v)} placeholder="Apple" testId="repair-make"/>
          <Field label="Model" value={f.device_model} onChange={(v) => set("device_model", v)} placeholder="MacBook Pro 14"/>
          <Field label="Serienummer / IMEI" value={f.device_serial} onChange={(v) => set("device_serial", v)}/>
        </div>
        <Field label="Toegangscode / wachtwoord (optioneel)" value={f.device_password} onChange={(v) => set("device_password", v)}/>

        <div>
          <label className="text-xs text-neutral-600 uppercase tracking-wider">Meegeleverde accessoires</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {ACCESSORY_OPTIONS.map((a) => {
              const on = f.accessories.includes(a);
              return (
                <button type="button" key={a} onClick={() => toggleAcc(a)}
                  className={`px-3 py-1.5 text-xs border ${on ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"}`}>
                  {a}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs text-neutral-600 uppercase tracking-wider">Klacht / omschrijving *</label>
          <textarea required rows={3} value={f.complaint} onChange={(e) => set("complaint", e.target.value)}
            className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
            data-testid="repair-complaint"/>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Prijsindicatie (€)" type="number" step="0.01" value={f.estimate} onChange={(v) => set("estimate", v)} placeholder="0"/>
          <Field label="Interne notities" value={f.notes} onChange={(v) => set("notes", v)}/>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900">Annuleren</button>
          <button type="submit" className="px-5 py-2 bg-neutral-900 text-white text-sm font-medium hover:bg-black" data-testid="repair-create">
            Aanmaken & foto's/handtekening toevoegen
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ===== detail dialog =====
const RepairDetailDialog = ({ repairId, secret, onClose }) => {
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPhase, setSavingPhase] = useState(null);
  const [phase, setPhase] = useState("intake"); // which phase to manage

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/repairs/${repairId}`, { params: { secret } });
      setR(res.data);
    } finally { setLoading(false); }
  }, [repairId, secret]);

  useEffect(() => { load(); }, [load]);

  const updateField = async (patch) => {
    const res = await axios.put(`${API}/admin/repairs/${repairId}`, patch, { params: { secret } });
    setR((prev) => ({ ...(prev || {}), ...res.data }));
  };

  const uploadPhoto = async (file, ph) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("phase", ph);
    try {
      await axios.post(`${API}/admin/repairs/${repairId}/photos`, fd, {
        params: { secret }, headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      alert("Upload mislukt: " + (e.response?.data?.detail || e.message));
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Foto verwijderen?")) return;
    await axios.delete(`${API}/admin/repairs/${repairId}/photos/${photoId}`, { params: { secret } });
    load();
  };

  const saveSignature = async (dataURL, ph) => {
    setSavingPhase(ph);
    try {
      await axios.post(`${API}/admin/repairs/${repairId}/signature`, { phase: ph, data: dataURL }, { params: { secret } });
      await load();
    } catch (e) {
      alert("Handtekening opslaan mislukt: " + (e.response?.data?.detail || e.message));
    } finally { setSavingPhase(null); }
  };

  const sendReceipt = async (ph) => {
    if (!window.confirm(`Bonnetje (${ph === "intake" ? "inname" : "retour"}) mailen naar klant?`)) return;
    try {
      await axios.post(`${API}/admin/repairs/${repairId}/send-receipt`, {}, { params: { secret, phase: ph } });
      alert("Bonnetje verzonden ✓");
      load();
    } catch (e) {
      alert("Versturen mislukt: " + (e.response?.data?.detail || e.message));
    }
  };

  const photoUrl = (fn) => `${API}/admin/repairs/${repairId}/photos/${fn}?secret=${encodeURIComponent(secret)}`;
  const receiptUrl = (ph) => `${API}/admin/repairs/${repairId}/receipt?phase=${ph}&secret=${encodeURIComponent(secret)}`;

  if (loading || !r) {
    return <Modal title="Reparatie" onClose={onClose} wide><div className="text-center py-10 text-neutral-500"><Loader2 size={16} className="inline animate-spin"/> Laden...</div></Modal>;
  }

  const photosIntake = (r.photos || []).filter((p) => p.phase === "intake");
  const photosReturn = (r.photos || []).filter((p) => p.phase === "return");
  const currentPhotos = phase === "intake" ? photosIntake : photosReturn;
  const currentSig = phase === "intake" ? r.signature_intake : r.signature_return;

  return (
    <Modal
      title={`Reparatie ${r.ticket_number} — ${r.customer?.name || ""}`}
      onClose={onClose} wide
    >
      {/* Top: info + status */}
      <div className="grid md:grid-cols-[1fr_220px] gap-6">
        <div className="space-y-3 text-sm">
          <InfoRow k="Apparaat" v={`${r.device_make || "-"} ${r.device_model || ""}`.trim()}/>
          <InfoRow k="Serienummer" v={r.device_serial || "-"}/>
          <InfoRow k="Accessoires" v={(r.accessories || []).join(", ") || "-"}/>
          <InfoRow k="Klacht" v={r.complaint}/>
          {r.device_password && <InfoRow k="Wachtwoord" v={r.device_password}/>}
          {r.estimate ? <InfoRow k="Prijsindicatie" v={fmtEur(r.estimate)}/> : null}
        </div>
        <div className="space-y-2">
          <label className="text-xs text-neutral-600 uppercase tracking-wider">Status</label>
          <select
            value={r.status}
            onChange={(e) => updateField({ status: e.target.value })}
            className="w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
            data-testid="repair-status-select"
          >
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <StatusPill s={r.status}/>
          {r.receipt_sent_at && (
            <p className="text-xs text-neutral-500 mt-2">
              Laatste bon verzonden: {new Date(r.receipt_sent_at).toLocaleString("nl-NL")}
            </p>
          )}
        </div>
      </div>

      {/* Phase tabs */}
      <div className="mt-6 border-t border-neutral-200 pt-5">
        <div className="flex gap-2 mb-5">
          {[
            { k: "intake", label: "Inname" },
            { k: "return", label: "Retour" },
          ].map((t) => (
            <button
              key={t.k} onClick={() => setPhase(t.k)}
              className={`px-4 py-2 text-sm border ${phase === t.k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"}`}
              data-testid={`repair-phase-${t.k}`}
            >{t.label}</button>
          ))}
        </div>

        {/* Photos */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-neutral-900 uppercase tracking-wider">Foto's</h3>
            <label className="cursor-pointer flex items-center gap-2 text-sm border border-neutral-300 px-3 py-1.5 hover:border-neutral-900" data-testid={`repair-upload-${phase}`}>
              <Upload size={14}/>Foto uploaden
              <input
                type="file" accept="image/*" capture="environment"
                onChange={(e) => { if (e.target.files?.[0]) { uploadPhoto(e.target.files[0], phase); e.target.value = ""; } }}
                className="hidden"
              />
            </label>
          </div>
          {currentPhotos.length === 0 ? (
            <p className="text-xs text-neutral-500">Nog geen foto's bij {phase === "intake" ? "inname" : "retour"}.</p>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {currentPhotos.map((p) => (
                <div key={p.id} className="relative group border border-neutral-200">
                  <img src={photoUrl(p.filename)} alt={p.filename} className="w-full h-28 object-cover" loading="lazy"/>
                  <button
                    onClick={() => deletePhoto(p.id)}
                    className="absolute top-1 right-1 bg-white/90 hover:bg-rose-600 hover:text-white text-neutral-700 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Verwijderen"
                  ><Trash2 size={12}/></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Signature */}
        <div className="mb-6">
          <h3 className="text-sm font-bold text-neutral-900 uppercase tracking-wider mb-3 flex items-center gap-2">
            <PenTool size={14}/>Handtekening klant
          </h3>
          {currentSig ? (
            <div className="inline-block border border-neutral-200 p-2 bg-neutral-50 mb-3">
              <img src={currentSig} alt="handtekening" className="max-h-28"/>
            </div>
          ) : (
            <p className="text-xs text-neutral-500 mb-3">Nog geen handtekening.</p>
          )}
          <SignaturePad
            onSave={(dataURL) => saveSignature(dataURL, phase)}
            saving={savingPhase === phase}
            key={`${phase}-${currentSig ? "has" : "none"}`}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 border-t border-neutral-200 pt-5">
          <a href={receiptUrl(phase)} target="_blank" rel="noreferrer"
             className="flex items-center gap-2 border border-neutral-300 px-4 py-2 text-sm hover:border-neutral-900"
             data-testid={`repair-pdf-${phase}`}
          ><Download size={14}/>PDF Reparatiebon ({phase === "intake" ? "inname" : "retour"})</a>
          <button
            onClick={() => sendReceipt(phase)}
            className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-black"
            data-testid={`repair-send-${phase}`}
          ><Send size={14}/>Bon naar klant mailen</button>
        </div>
      </div>
    </Modal>
  );
};

// ===== signature pad =====
const SignaturePad = ({ onSave, saving }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  // Sizing on mount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.offsetWidth;
    const height = 180;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#171717";
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const save = () => {
    if (!hasInk) { alert("Laat de klant eerst tekenen."); return; }
    const canvas = canvasRef.current;
    // Flatten to white bg for PDF legibility
    const out = document.createElement("canvas");
    out.width = canvas.width; out.height = canvas.height;
    const octx = out.getContext("2d");
    octx.fillStyle = "#ffffff";
    octx.fillRect(0, 0, out.width, out.height);
    octx.drawImage(canvas, 0, 0);
    onSave(out.toDataURL("image/png"));
  };

  return (
    <div>
      <div className="border border-neutral-300 bg-white select-none" data-testid="signature-pad">
        <canvas
          ref={canvasRef}
          className="w-full touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
      </div>
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={clear} className="text-sm text-neutral-600 hover:text-neutral-900" data-testid="signature-clear">
          Wissen
        </button>
        <button type="button" onClick={save} disabled={saving}
          className="ml-auto bg-neutral-900 text-white px-4 py-1.5 text-sm font-medium hover:bg-black disabled:opacity-50"
          data-testid="signature-save"
        >{saving ? "Opslaan..." : "Handtekening opslaan"}</button>
      </div>
    </div>
  );
};

// ===== helpers =====
const InfoRow = ({ k, v }) => (
  <div className="grid grid-cols-[140px_1fr] gap-2">
    <div className="text-neutral-500 text-xs uppercase tracking-wider pt-0.5">{k}</div>
    <div className="text-neutral-900">{v}</div>
  </div>
);

const Field = ({ label, value, onChange, type = "text", placeholder = "", step, testId }) => (
  <div>
    <label className="text-xs text-neutral-600 uppercase tracking-wider">{label}</label>
    <input
      type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} step={step} data-testid={testId}
      className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
    />
  </div>
);

const Modal = ({ title, onClose, children, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/50 p-4 overflow-y-auto">
    <div className={`bg-white w-full ${wide ? "max-w-4xl" : "max-w-2xl"} my-8`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
        <h2 className="text-lg font-bold text-neutral-900">{title}</h2>
        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="repair-modal-close"><X size={18}/></button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

export default RepairsTab;
